import { useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Crown, ArrowLeft, Loader2, CheckCircle2, Building2, LineChart, Warehouse, Award, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const STEPS = [
  { n: 1, key: "identity",     label: "Corporate identity",  Icon: Building2 },
  { n: 2, key: "financial",    label: "Financial power",     Icon: LineChart },
  { n: 3, key: "infra",        label: "Infrastructure",      Icon: Warehouse },
  { n: 4, key: "record",       label: "Track record",        Icon: Award },
  { n: 5, key: "compliance",   label: "Compliance & sign-off", Icon: ShieldCheck },
];

const INITIAL = {
  // Step 1
  legal_entity_name: "", incorporation_year: "", cin_number: "", gstin: "",
  pan_number: "", hq_address: "", directors: "", ubo_name: "", group_structure: "",
  // Step 2
  audited_turnover_inr: "", audited_turnover_source: "",
  turnover_prev1_inr: "", turnover_prev2_inr: "",
  ebitda_inr: "", networth_inr: "", working_capital_inr: "",
  banker_name: "", banker_branch: "", banker_rm_name: "", banker_rm_contact: "",
  // Step 3
  storage_capacity_mt: "", warehouse_locations: "",
  fleet_size: "", fleet_owned: "", fleet_leased: "",
  inhouse_labour_count: "", third_party_logistics: "",
  // Step 4
  trades_completed: "", key_projects_delivered: "",
  marquee_project_1: "", marquee_project_2: "", marquee_project_3: "",
  marquee_project_4: "", marquee_project_5: "",
  safety_audit_date: "", safety_kpi_ltifr: "",
  iso_cert_no: "", bis_cert_no: "", msme_cert_no: "",
  balance_sheet_summary: "",
  // Step 5
  open_litigations: "", tax_standing: "",
  sanction_self_declaration: false,
  signatory_name: "", signatory_designation: "",
  signatory_email: "", signatory_din: "",
  accepts_lockin: false, accepts_code_of_conduct: false,
};

export default function BigCapApply() {
  const { user, api } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState(INITIAL);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const canNext = useMemo(() => ({
    1: !!(f.legal_entity_name && f.incorporation_year && f.gstin && f.pan_number
          && f.hq_address && f.directors && f.ubo_name),
    2: !!(f.audited_turnover_inr && f.audited_turnover_source
          && f.ebitda_inr !== "" && f.networth_inr !== ""
          && f.banker_name && f.banker_rm_name),
    3: !!(f.storage_capacity_mt && f.warehouse_locations
          && f.fleet_size !== "" && f.fleet_owned !== ""),
    4: !!(f.trades_completed !== "" && f.key_projects_delivered.length > 6
          && f.marquee_project_1 && f.iso_cert_no
          && f.balance_sheet_summary.length > 8),
    5: !!(f.signatory_name && f.signatory_designation && f.signatory_email
          && f.accepts_lockin && f.accepts_code_of_conduct
          && f.sanction_self_declaration),
  }), [f]);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;

  const submit = async () => {
    if (!canNext[5]) { toast.error("Please complete all mandatory fields."); return; }
    setBusy(true);
    try {
      await api.post("/big-cap/apply", {
        // Step 1
        legal_entity_name: f.legal_entity_name.trim(),
        incorporation_year: Number(f.incorporation_year) || null,
        cin_number: f.cin_number.trim(),
        gstin: f.gstin.trim(),
        pan_number: f.pan_number.trim(),
        hq_address: f.hq_address.trim(),
        directors: f.directors.trim(),
        ubo_name: f.ubo_name.trim(),
        group_structure: f.group_structure.trim(),
        // Step 2
        audited_turnover_inr: Number(f.audited_turnover_inr),
        audited_turnover_source: f.audited_turnover_source.trim(),
        turnover_prev1_inr: numOrNull(f.turnover_prev1_inr),
        turnover_prev2_inr: numOrNull(f.turnover_prev2_inr),
        ebitda_inr: numOrNull(f.ebitda_inr),
        networth_inr: numOrNull(f.networth_inr),
        working_capital_inr: numOrNull(f.working_capital_inr),
        banker_name: f.banker_name.trim(),
        banker_branch: f.banker_branch.trim(),
        banker_rm_name: f.banker_rm_name.trim(),
        banker_rm_contact: f.banker_rm_contact.trim(),
        // Step 3
        storage_capacity_mt: Number(f.storage_capacity_mt),
        warehouse_locations: f.warehouse_locations.trim(),
        fleet_size: Number(f.fleet_size),
        fleet_owned: numOrNull(f.fleet_owned),
        fleet_leased: numOrNull(f.fleet_leased),
        inhouse_labour_count: numOrNull(f.inhouse_labour_count),
        third_party_logistics: f.third_party_logistics.trim(),
        // Step 4
        trades_completed: Number(f.trades_completed),
        key_projects_delivered: f.key_projects_delivered.trim(),
        marquee_project_1: f.marquee_project_1.trim(),
        marquee_project_2: f.marquee_project_2.trim(),
        marquee_project_3: f.marquee_project_3.trim(),
        marquee_project_4: f.marquee_project_4.trim(),
        marquee_project_5: f.marquee_project_5.trim(),
        safety_audit_date: f.safety_audit_date,
        safety_kpi_ltifr: numOrNull(f.safety_kpi_ltifr),
        iso_cert_no: f.iso_cert_no.trim(),
        bis_cert_no: f.bis_cert_no.trim(),
        msme_cert_no: f.msme_cert_no.trim(),
        balance_sheet_summary: f.balance_sheet_summary.trim(),
        // Step 5
        open_litigations: f.open_litigations.trim(),
        tax_standing: f.tax_standing.trim(),
        sanction_self_declaration: f.sanction_self_declaration,
        signatory_name: f.signatory_name.trim(),
        signatory_designation: f.signatory_designation.trim(),
        signatory_email: f.signatory_email.trim(),
        signatory_din: f.signatory_din.trim(),
        accepts_lockin: f.accepts_lockin,
        accepts_code_of_conduct: f.accepts_code_of_conduct,
      });
      toast.success("Application submitted · under Vinci executive scrutiny");
      nav("/league");
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  const StepIcon = STEPS[step - 1].Icon;

  return (
    <div className="min-h-screen bg-[#050506] text-[#F5F5F0]">
      <Toaster theme="dark" richColors position="top-right" />
      <Header />
      <main className="max-w-5xl mx-auto px-6 md:px-12 pt-28 pb-24">
        <Link to="/league" className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#D8B23E] mb-6">
          <ArrowLeft size={12}/> Back to league
        </Link>

        <div className="flex items-start gap-4 border-b border-white/10 pb-6 mb-8">
          <div className="h-12 w-12 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] flex items-center justify-center shrink-0">
            <Crown size={20}/>
          </div>
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Elite Certification · Step {step} of 5</div>
            <h1 className="font-serif text-3xl">Big-Cap Application.</h1>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              Vinci leadership manually verifies every field, banker reference and marquee project you submit here.
              Decision within <strong>10 business days</strong>. Incomplete or inaccurate submissions are rejected outright.
            </p>
          </div>
        </div>

        {/* Progress rail */}
        <div className="grid grid-cols-5 gap-2 mb-8">
          {STEPS.map(s => {
            const active = step === s.n;
            const done = step > s.n;
            const S = s.Icon;
            return (
              <button key={s.n} data-testid={`step-pill-${s.n}`}
                      onClick={() => (canNext[s.n - 1] !== false && s.n <= step) && setStep(s.n)}
                      className={`text-left border rounded-sm p-3 transition-colors ${
                        active ? "border-[#D8B23E] bg-[#D8B23E]/10" :
                        done ? "border-[#3DBB84]/40 bg-[#3DBB84]/[0.06]" :
                        "border-white/10 bg-black/30 opacity-60"
                      }`}>
                <div className="flex items-center gap-2">
                  <S size={14} className={active ? "text-[#D8B23E]" : done ? "text-[#3DBB84]" : "text-[#8A8A82]"}/>
                  {done && <CheckCircle2 size={11} className="text-[#3DBB84]"/>}
                </div>
                <div className={`mt-1 font-grotesk text-[9px] uppercase tracking-[0.2em] ${active ? "text-[#D8B23E]" : "text-[#8A8A82]"}`}>Step {s.n}</div>
                <div className={`font-body text-[11px] ${active ? "text-[#F5F5F0]" : "text-[#B8B8B0]"}`}>{s.label}</div>
              </button>
            );
          })}
        </div>

        {/* Step body */}
        <div className="space-y-6">
          {step === 1 && (
            <Section title="Corporate identity" Icon={StepIcon}>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Legal entity (as per MoA) *" v={f.legal_entity_name} onChange={v => set("legal_entity_name", v)} testid="bc-entity"/>
                <Field label="Year of incorporation *" type="number" v={f.incorporation_year} onChange={v => set("incorporation_year", v)} testid="bc-inc-year"/>
                <Field label="CIN" v={f.cin_number} onChange={v => set("cin_number", v)} testid="bc-cin" placeholder="U45201MH2010PTC000000"/>
                <Field label="GSTIN *" v={f.gstin} onChange={v => set("gstin", v)} testid="bc-gstin"/>
                <Field label="PAN *" v={f.pan_number} onChange={v => set("pan_number", v)} testid="bc-pan"/>
                <Field label="UBO — Ultimate beneficial owner *" v={f.ubo_name} onChange={v => set("ubo_name", v)} testid="bc-ubo"/>
              </div>
              <TextArea label="HQ / registered address *" v={f.hq_address} onChange={v => set("hq_address", v)} rows={2} testid="bc-hq"/>
              <TextArea label="Board of directors (one per line, or CSV) *" v={f.directors} onChange={v => set("directors", v)} rows={3} testid="bc-directors"/>
              <TextArea label="Group / corporate structure (2-3 lines)" v={f.group_structure} onChange={v => set("group_structure", v)} rows={2} testid="bc-group"/>
            </Section>
          )}

          {step === 2 && (
            <Section title="Financial power" Icon={StepIcon}>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Audited turnover — FY-25 (INR) *" type="number" v={f.audited_turnover_inr} onChange={v => set("audited_turnover_inr", v)} testid="bc-turnover"/>
                <Field label="Turnover FY-24 (INR)" type="number" v={f.turnover_prev1_inr} onChange={v => set("turnover_prev1_inr", v)} testid="bc-turnover-prev1"/>
                <Field label="Turnover FY-23 (INR)" type="number" v={f.turnover_prev2_inr} onChange={v => set("turnover_prev2_inr", v)} testid="bc-turnover-prev2"/>
                <Field label="EBITDA — FY-25 (INR) *" type="number" v={f.ebitda_inr} onChange={v => set("ebitda_inr", v)} testid="bc-ebitda"/>
                <Field label="Net worth (INR) *" type="number" v={f.networth_inr} onChange={v => set("networth_inr", v)} testid="bc-networth"/>
                <Field label="Working capital (INR)" type="number" v={f.working_capital_inr} onChange={v => set("working_capital_inr", v)} testid="bc-wc"/>
                <Field label="Auditor firm / source *" v={f.audited_turnover_source} onChange={v => set("audited_turnover_source", v)} testid="bc-audit-src"/>
              </div>
              <div className="mt-4 border border-white/10 rounded-sm bg-black/30 p-4">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E] mb-3">Primary banker reference</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="Banker name *" v={f.banker_name} onChange={v => set("banker_name", v)} testid="bc-banker"/>
                  <Field label="Branch" v={f.banker_branch} onChange={v => set("banker_branch", v)} testid="bc-branch"/>
                  <Field label="Relationship manager *" v={f.banker_rm_name} onChange={v => set("banker_rm_name", v)} testid="bc-rm"/>
                  <Field label="RM contact (phone/email)" v={f.banker_rm_contact} onChange={v => set("banker_rm_contact", v)} testid="bc-rm-contact"/>
                </div>
              </div>
            </Section>
          )}

          {step === 3 && (
            <Section title="Infrastructure & logistics" Icon={StepIcon}>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Owned storage (MT) *" type="number" v={f.storage_capacity_mt} onChange={v => set("storage_capacity_mt", v)} testid="bc-storage"/>
                <Field label="Fleet size *" type="number" v={f.fleet_size} onChange={v => set("fleet_size", v)} testid="bc-fleet"/>
                <Field label="Fleet — owned *" type="number" v={f.fleet_owned} onChange={v => set("fleet_owned", v)} testid="bc-fleet-owned"/>
                <Field label="Fleet — leased" type="number" v={f.fleet_leased} onChange={v => set("fleet_leased", v)} testid="bc-fleet-leased"/>
                <Field label="In-house labour headcount" type="number" v={f.inhouse_labour_count} onChange={v => set("inhouse_labour_count", v)} testid="bc-labour"/>
              </div>
              <TextArea label="Warehouse locations (city / state list) *" v={f.warehouse_locations} onChange={v => set("warehouse_locations", v)} rows={2} testid="bc-warehouses"/>
              <TextArea label="3PL partners (name · scope · years)" v={f.third_party_logistics} onChange={v => set("third_party_logistics", v)} rows={2} testid="bc-3pl"/>
            </Section>
          )}

          {step === 4 && (
            <Section title="Track record & compliance certificates" Icon={StepIcon}>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Successful Vinci trades completed *" type="number" v={f.trades_completed} onChange={v => set("trades_completed", v)} testid="bc-trades"/>
                <Field label="Safety KPI · LTIFR (per 1M hrs)" type="number" v={f.safety_kpi_ltifr} onChange={v => set("safety_kpi_ltifr", v)} testid="bc-ltifr"/>
                <Field label="Last safety audit date" type="date" v={f.safety_audit_date} onChange={v => set("safety_audit_date", v)} testid="bc-safety"/>
                <Field label="ISO certification # *" v={f.iso_cert_no} onChange={v => set("iso_cert_no", v)} testid="bc-iso"/>
                <Field label="BIS certification #" v={f.bis_cert_no} onChange={v => set("bis_cert_no", v)} testid="bc-bis"/>
                <Field label="MSME / Udyam #" v={f.msme_cert_no} onChange={v => set("msme_cert_no", v)} testid="bc-msme"/>
              </div>
              <div className="mt-4 border border-white/10 rounded-sm bg-black/30 p-4 space-y-2">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#D8B23E]">Five marquee projects delivered (last 5 years)</div>
                {[1,2,3,4,5].map(n => (
                  <Field key={n} label={`Project ${n} · name · employer · scope · tonnage · value · year${n===1?' *':''}`}
                         v={f[`marquee_project_${n}`]} onChange={v => set(`marquee_project_${n}`, v)}
                         testid={`bc-marquee-${n}`} placeholder="e.g. NHAI · Delhi-Meerut Expressway · 60,000 MT TMT · ₹380 Cr · 2024"/>
                ))}
              </div>
              <TextArea label="Key projects delivered (short paragraph) *"
                        v={f.key_projects_delivered} onChange={v => set("key_projects_delivered", v)} rows={3} testid="bc-projects"/>
              <TextArea label="Balance sheet summary (3-yr turnover / EBITDA / debt / working capital) *"
                        v={f.balance_sheet_summary} onChange={v => set("balance_sheet_summary", v)} rows={4} testid="bc-balance-sheet"/>
            </Section>
          )}

          {step === 5 && (
            <Section title="Compliance, signatory & declarations" Icon={StepIcon}>
              <TextArea label="Open litigations (case #, court, amount involved)"
                        v={f.open_litigations} onChange={v => set("open_litigations", v)} rows={2} testid="bc-litigations"/>
              <TextArea label="Tax standing (any open notices, settlements, orders)"
                        v={f.tax_standing} onChange={v => set("tax_standing", v)} rows={2} testid="bc-tax"/>

              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Signatory name *" v={f.signatory_name} onChange={v => set("signatory_name", v)} testid="bc-signatory"/>
                <Field label="Signatory designation *" v={f.signatory_designation} onChange={v => set("signatory_designation", v)} testid="bc-designation"/>
                <Field label="Signatory email *" type="email" v={f.signatory_email} onChange={v => set("signatory_email", v)} testid="bc-signatory-email"/>
                <Field label="Signatory DIN (if director)" v={f.signatory_din} onChange={v => set("signatory_din", v)} testid="bc-din"/>
              </div>

              <label className="mt-4 flex items-start gap-3 border border-white/10 rounded-sm p-3 bg-black/30 cursor-pointer">
                <input data-testid="bc-sanction" type="checkbox" checked={f.sanction_self_declaration}
                       onChange={e => set("sanction_self_declaration", e.target.checked)}
                       className="mt-1 accent-[#D8B23E]"/>
                <span className="font-body text-sm text-[#F5F5F0]">
                  <strong>Sanctions self-declaration.</strong> I confirm the applicant entity, its UBOs, directors, and material shareholders are <em>not</em> on any UN, OFAC, EU, RBI or Indian MHA sanctions list, nor debarred from public procurement in India.
                </span>
              </label>

              <label className="flex items-start gap-3 border border-white/10 rounded-sm p-3 bg-black/30 cursor-pointer">
                <input data-testid="bc-code" type="checkbox" checked={f.accepts_code_of_conduct}
                       onChange={e => set("accepts_code_of_conduct", e.target.checked)}
                       className="mt-1 accent-[#D8B23E]"/>
                <span className="font-body text-sm text-[#F5F5F0]">
                  I accept the <strong>Vinci Big-Cap Code of Conduct</strong> and agree to e-sign it via Zoho Sign once inducted.
                </span>
              </label>

              <label className="flex items-start gap-3 border border-white/10 rounded-sm p-3 bg-black/30 cursor-pointer">
                <input data-testid="bc-lockin" type="checkbox" checked={f.accepts_lockin}
                       onChange={e => set("accepts_lockin", e.target.checked)}
                       className="mt-1 accent-[#D8B23E]"/>
                <span className="font-body text-sm text-[#F5F5F0]">
                  I accept the <strong>36-month lock-in</strong> on the <strong>₹25,00,000 refundable security deposit</strong>. Deposit is released only after 36 months from induction, subject to League conduct.
                </span>
              </label>

              <div className="mt-5 border border-[#D8B23E]/40 rounded-sm bg-[#D8B23E]/[0.06] p-4">
                <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Executive scrutiny summary</div>
                <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 font-body text-[12px] text-[#F5F5F0]">
                  <div>Entity: <strong>{f.legal_entity_name || "—"}</strong></div>
                  <div>Turnover FY-25: <strong>₹{Number(f.audited_turnover_inr || 0).toLocaleString("en-IN")}</strong></div>
                  <div>Storage: <strong>{Number(f.storage_capacity_mt || 0).toLocaleString("en-IN")} MT</strong></div>
                  <div>Fleet: <strong>{Number(f.fleet_size || 0).toLocaleString("en-IN")} vehicles</strong></div>
                  <div>Trades delivered: <strong>{f.trades_completed || 0}</strong></div>
                  <div>Signatory: <strong>{f.signatory_name || "—"}</strong></div>
                </div>
              </div>
            </Section>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-white/10 pt-6 mt-8">
          {step > 1 ? (
            <button onClick={() => setStep(s => s - 1)} data-testid="prev-step"
                    className="border border-white/15 hover:border-[#D8B23E] text-[#8A8A82] hover:text-[#F5F5F0] rounded-sm px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em]">
              Back
            </button>
          ) : <span/>}
          {step < 5 ? (
            <button onClick={() => canNext[step] && setStep(s => s + 1)} disabled={!canNext[step]} data-testid="next-step"
                    className={`rounded-sm px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em] ${
                      canNext[step] ? "bg-[#D8B23E] hover:bg-[#c5a233] text-black" : "bg-white/5 text-[#8A8A82] cursor-not-allowed"
                    }`}>
              Continue
            </button>
          ) : (
            <button onClick={submit} disabled={!canNext[5] || busy} data-testid="submit-bigcap"
                    className={`inline-flex items-center gap-2 rounded-sm px-5 py-3 font-grotesk text-[10px] uppercase tracking-[0.24em] ${
                      canNext[5] && !busy ? "bg-[#D8B23E] hover:bg-[#c5a233] text-black" : "bg-white/5 text-[#8A8A82] cursor-not-allowed"
                    }`}>
              {busy ? <Loader2 size={12} className="animate-spin"/> : <Crown size={12}/>}
              Submit for executive scrutiny
            </button>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, Icon, children }) {
  return (
    <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5 space-y-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-[#D8B23E]"/>}
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, v, onChange, type = "text", testid, placeholder = "" }) {
  return (
    <div>
      <div className="font-body text-[11px] text-[#8A8A82] mb-1">{label}</div>
      <input data-testid={testid} type={type} value={v ?? ""} placeholder={placeholder}
             onChange={e => onChange(e.target.value)}
             className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
    </div>
  );
}

function TextArea({ label, v, onChange, rows = 3, testid }) {
  return (
    <div>
      <div className="font-body text-[11px] text-[#8A8A82] mb-1">{label}</div>
      <textarea data-testid={testid} rows={rows} value={v ?? ""} onChange={e => onChange(e.target.value)}
                className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
    </div>
  );
}

function numOrNull(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
